import styled, { css } from 'styled-components';
import { HEADER_HEIGHT, SEARCH_WIDGET_HEIGHT, FILTERS_HEIGHT } from '@/app/common/constants';
import type { Brand } from '@/domainLogic/index.ts';
import { isIndexSelector, isListSelector, isMapSelector } from '@/app/common/css/selectors.ts';
import { headerHeightCss } from '@/app/common/headerFooter/headerHeight.ts';
import customCss from './hotel-detail/css/customCss';

// base styles
const baseStyles = (brand: Brand) => css`
  margin-top: ${headerHeightCss(HEADER_HEIGHT(brand, true))};
`;

const indexStyles = css`
  ${isIndexSelector} & {
    margin-top: ${headerHeightCss(64)};

    @media ${customCss.breakpoints.lg} {
      margin-top: 136px;
    }
  }
`;

const listStyles = css`
  ${isListSelector} & {
    @media (min-width: 992px) {
      margin-top: 64px;
    }
  }
`;

// map view styles
const mapStyles = css`
  ${isMapSelector} & {
    margin-top: ${headerHeightCss(64)};

    @media ${customCss.breakpoints.lg} {
      margin-top: 136px;
    }

    @media ${customCss.breakpoints.xl} {
      margin-top: 136px;
    }
  }
`;

const searchPageStyles = css`
  ${indexStyles};
  ${listStyles};
  ${mapStyles};
`;

const externalHeaderBaseStyles = (brand: Brand) => css`
  margin-top: ${HEADER_HEIGHT(brand, true)}px;

  @media ${customCss.breakpoints.sm} {
    margin-top: ${HEADER_HEIGHT(brand, false)}px;
  }
`;

const externalHeaderMapStyles = (brand: Brand) => css`
  ${isMapSelector} & {
    @media ${customCss.breakpoints.mdPlus} {
      margin-top: ${HEADER_HEIGHT(brand, false) + SEARCH_WIDGET_HEIGHT + FILTERS_HEIGHT}px;
    }
  }
`;

const externalHeaderListStyles = (brand: Brand, evolutionEnabled?: boolean) => css`
  ${isListSelector} & {
    margin-top: ${evolutionEnabled ? 0 : HEADER_HEIGHT(brand, true) + SEARCH_WIDGET_HEIGHT}px;

    @media ${customCss.breakpoints.sm} {
      margin-top: ${HEADER_HEIGHT(brand, false) + SEARCH_WIDGET_HEIGHT}px;
    }
  }
`;

const externalHeaderIndexStyles = (brand: Brand) => css`
  ${isIndexSelector} & {
    margin-top: ${HEADER_HEIGHT(brand, true) + SEARCH_WIDGET_HEIGHT + 24}px;

    @media ${customCss.breakpoints.sm} {
      margin-top: ${HEADER_HEIGHT(brand, false) + SEARCH_WIDGET_HEIGHT}px;
    }

    @media ${customCss.breakpoints.lg} {
      margin-top: ${HEADER_HEIGHT(brand, false) + SEARCH_WIDGET_HEIGHT + FILTERS_HEIGHT}px;
    }
  }
`;

const externalHeaderSearchPageStyles = (brand: Brand, evolutionEnabled?: boolean) => css`
  ${externalHeaderListStyles(brand, evolutionEnabled)};
  ${externalHeaderMapStyles(brand)};
  ${externalHeaderIndexStyles(brand)};
`;

// The detail steps have no list/index/map body class, so the search-page overrides above never
// apply there; when the fixed external header also carries the CSW (MPH-864) the content has to
// clear the widget's height too.
const externalHeaderDetailStyles = (brand: Brand) => css`
  margin-top: ${HEADER_HEIGHT(brand, true) + SEARCH_WIDGET_HEIGHT}px;

  @media ${customCss.breakpoints.sm} {
    margin-top: ${HEADER_HEIGHT(brand, false) + SEARCH_WIDGET_HEIGHT}px;
  }
`;

export const TopMargin = styled.div<{
  externalHeaderEnabled: boolean;
  brand: Brand;
  evolutionEnabled?: boolean;
  showExternalCSWDetail?: boolean;
}>`
  ${({ brand }) =>
    brand &&
    css`
      ${baseStyles(brand)};
      ${searchPageStyles};
    `}

  ${({ externalHeaderEnabled, brand, evolutionEnabled }) =>
    externalHeaderEnabled &&
    css`
      ${externalHeaderBaseStyles(brand)};
      ${externalHeaderSearchPageStyles(brand, evolutionEnabled)};
    `};

  ${({ externalHeaderEnabled, brand, showExternalCSWDetail }) =>
    externalHeaderEnabled &&
    showExternalCSWDetail &&
    css`
      ${externalHeaderDetailStyles(brand)};
    `};

  ${({ evolutionEnabled }) =>
    evolutionEnabled &&
    css`
      @media (max-width: 767px) {
        margin-top: 0;
      }
    `}
`;
